import { type AlmanacGroup } from "portal/components/categoryTemplates/useAlmanacOptions";
import { AlmanacMostSimilarFields } from "portal/components/categoryTemplates/createCategory/AlmanacMostSimilarFields";
import { Category, Role } from "protos/robot_syncer/plant_profile";
import { CategoryNameAndRoleFields } from "./CategoryNameAndRoleFields";
import {
  chipIdSetInProfile,
  UnsavedPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import {
  CHOICE_NEW,
  CHOICE_TEMPLATE,
  CreateCategoryChooseStep,
} from "./CreateCategoryChooseStep";
import { CopyFromTemplateConfigureStep } from "./CopyFromTemplateConfigureStep";
import { CopyFromTemplatePickStep } from "./CopyFromTemplatePickStep";
import { CreateCategoryDialogActions } from "./CreateCategoryDialogActions";
import {
  type CreateCategoryFormValues,
  useCreateCategoryForm,
} from "./useCreateCategoryForm";
import { Dialog, DialogContent, DialogTitle, Skeleton } from "@mui/material";
import { Form, Formik, type FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

type CreateCategoryFormFieldsProps = {
  values: Pick<CreateCategoryFormValues, "role" | "mostSimilarTo">;
  setFieldValue: FormikHelpers<CreateCategoryFormValues>["setFieldValue"];
  setFieldTouched: FormikHelpers<CreateCategoryFormValues>["setFieldTouched"];
  almanacGroups: AlmanacGroup[];
  defaultAlmanacId: string;
};

export const CreateCategoryFormFields: FC<CreateCategoryFormFieldsProps> = ({
  values,
  setFieldValue,
  setFieldTouched,
  almanacGroups,
  defaultAlmanacId,
}) => (
  <>
    <CategoryNameAndRoleFields
      role={values.role}
      mostSimilarTo={values.mostSimilarTo}
      setFieldValue={setFieldValue}
      setFieldTouched={setFieldTouched}
    />
    <AlmanacMostSimilarFields
      almanacGroups={almanacGroups}
      defaultAlmanacId={defaultAlmanacId}
    />
  </>
);

export const STEP_CHOOSE = "choose";
export const STEP_FROM_TEMPLATE_CONFIGURE = "fromTemplateConfigure";

type CreateCategoryDialogStep =
  | { step: typeof STEP_CHOOSE }
  | { step: typeof CHOICE_NEW }
  | { step: typeof CHOICE_TEMPLATE }
  | { step: typeof STEP_FROM_TEMPLATE_CONFIGURE; category: Category };

interface CreateCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  unsavedPlantProfile: UnsavedPlantProfile;
  customerUuid?: string;
  defaultRole?: Role;
  onAdd: (category: Category, role: Role) => void;
}

export const CreateCategoryDialog: FC<CreateCategoryDialogProps> = ({
  open,
  onClose,
  unsavedPlantProfile,
  customerUuid,
  defaultRole,
  onAdd,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<CreateCategoryDialogStep>({
    step: STEP_CHOOSE,
  });
  const [pickedCategory, setPickedCategory] = useState<Category | undefined>();
  const [selectedChipIds, setSelectedChipIds] = useState<Set<string>>(
    () => new Set()
  );
  const ineligibleChipIds = useMemo(
    () => chipIdSetInProfile(unsavedPlantProfile.categories),
    [unsavedPlantProfile.categories]
  );

  const reset = useCallback(() => {
    setStep({ step: STEP_CHOOSE });
    setPickedCategory(undefined);
    setSelectedChipIds(new Set());
  }, []);

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSelectTemplate = useCallback(
    (category: Category) => {
      if (pickedCategory?.id !== category.id) {
        const selected = new Set<string>();
        for (const id of category.chipIds) {
          if (id && !ineligibleChipIds.has(id)) {
            selected.add(id);
          }
        }
        setSelectedChipIds(selected);
      }
      setPickedCategory(category);
    },
    [ineligibleChipIds, pickedCategory?.id]
  );

  const handleToggleChip = useCallback(
    (chipId: string, isSelected: boolean) => {
      if (ineligibleChipIds.has(chipId)) {
        return;
      }
      setSelectedChipIds((current) => {
        const next = new Set(current);
        if (isSelected) {
          next.add(chipId);
        } else {
          next.delete(chipId);
        }
        return next;
      });
    },
    [ineligibleChipIds]
  );

  const handleAdd = useCallback(
    (category: Category, role: Role): void => {
      onAdd(category, role);
      handleClose();
    },
    [handleClose, onAdd]
  );

  const { almanacGroups, defaultAlmanacId, formConfig, isLoading } =
    useCreateCategoryForm(
      unsavedPlantProfile,
      customerUuid,
      handleAdd,
      defaultRole
    );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("components.categoryCollectionProfile.actions.addNewCategory")}
      </DialogTitle>
      {open && (
        <DialogContent>
          {step.step === STEP_CHOOSE && (
            <CreateCategoryChooseStep
              onCancel={handleClose}
              onNext={(choice) => setStep({ step: choice })}
            />
          )}
          {step.step === CHOICE_NEW &&
            (isLoading ? (
              <div className="flex flex-col gap-4">
                <Skeleton
                  variant="rectangular"
                  className="h-14 w-full rounded"
                />
              </div>
            ) : (
              <Formik {...formConfig}>
                {({
                  values,
                  submitForm,
                  isSubmitting,
                  dirty,
                  isValid,
                  setFieldValue,
                  setFieldTouched,
                }) => (
                  <Form>
                    <div className="flex flex-col gap-4">
                      <CreateCategoryFormFields
                        values={values}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        almanacGroups={almanacGroups}
                        defaultAlmanacId={defaultAlmanacId}
                      />
                    </div>
                    <CreateCategoryDialogActions
                      onBack={() => setStep({ step: STEP_CHOOSE })}
                      onCancel={handleClose}
                      onConfirm={submitForm}
                      confirmLabel={t("utils.actions.add")}
                      confirmDisabled={!dirty || !isValid}
                      confirmLoading={isSubmitting}
                      disabled={isSubmitting}
                    />
                  </Form>
                )}
              </Formik>
            ))}
          {step.step === CHOICE_TEMPLATE && (
            <CopyFromTemplatePickStep
              customerUuid={customerUuid}
              selectedCategory={pickedCategory}
              ineligibleChipIds={ineligibleChipIds}
              onSelectCategory={handleSelectTemplate}
              onCancel={handleClose}
              onBack={() => setStep({ step: STEP_CHOOSE })}
              onNext={() => {
                if (pickedCategory) {
                  setStep({
                    step: STEP_FROM_TEMPLATE_CONFIGURE,
                    category: pickedCategory,
                  });
                }
              }}
            />
          )}
          {step.step === STEP_FROM_TEMPLATE_CONFIGURE && (
            <CopyFromTemplateConfigureStep
              source={step.category}
              defaultRole={defaultRole}
              unsavedPlantProfile={unsavedPlantProfile}
              ineligibleChipIds={ineligibleChipIds}
              selectedChipIds={selectedChipIds}
              onToggleChip={handleToggleChip}
              onAdd={handleAdd}
              onCancel={handleClose}
              onBack={() => setStep({ step: CHOICE_TEMPLATE })}
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};
